/**
 * useSupportItems
 *
 * Returns the static 20-entry Support catalog used by the Support list page
 * (`/support`) and detail page (`/support/:id`). Mirrors Flutter's
 * `SupportUtils.items` (lib/presentation/utils/support_utils.dart:7-108) and
 * preserves Flutter's declared order exactly — `id` matches the array index
 * because Flutter routes the detail screen by index
 * (`support_screen.dart:115-116`, `support_item_screen.dart:10`).
 *
 * Navigation is intentionally not part of the entity — pages own the
 * `id → navigate` call and the privacy-policy detail-page special case.
 */

import { useMemo } from 'react';
import { SupportCategory } from '@/domain/enums/support/supportCategory';
import type { SupportCategoryItemEntity } from '@/domain/entities/support/supportCategoryItemEntity';
import { useLocale } from '@/presentation/contexts/LocaleContext';

export const useSupportItems = (): SupportCategoryItemEntity[] => {
    const { l10n } = useLocale();
    const items = l10n.support.items;

    return useMemo(() => [
        {
            id: 0,
            category: SupportCategory.account,
            title: items.createAccount.title,
            description: items.createAccount.description,
        },
        {
            id: 1,
            category: SupportCategory.account,
            title: items.deleteAccount.title,
            description: items.deleteAccount.description,
        },
        {
            id: 2,
            category: SupportCategory.account,
            title: items.profileSetup.title,
            description: items.profileSetup.description,
        },
        {
            id: 3,
            category: SupportCategory.appUsage,
            title: items.uploadContent.title,
            description: items.uploadContent.description,
        },
        {
            id: 4,
            category: SupportCategory.appUsage,
            title: items.subscribeToOthers.title,
            description: items.subscribeToOthers.description,
        },
        {
            id: 5,
            category: SupportCategory.appUsage,
            title: items.sendGifts.title,
            description: items.sendGifts.description,
        },
        {
            id: 6,
            category: SupportCategory.appUsage,
            title: items.directMessaging.title,
            description: items.directMessaging.description,
        },
        {
            id: 7,
            category: SupportCategory.appUsage,
            title: items.communities.title,
            description: items.communities.description,
        },
        {
            id: 8,
            category: SupportCategory.appUsage,
            title: items.directVideoCalls.title,
            description: items.directVideoCalls.description,
        },
        {
            id: 9,
            category: SupportCategory.appUsage,
            title: items.liveSessions.title,
            description: items.liveSessions.description,
        },
        {
            id: 10,
            category: SupportCategory.premium,
            title: items.premiumPackages.title,
            description: items.premiumPackages.description,
        },
        {
            id: 11,
            category: SupportCategory.wallet,
            title: items.buyCoins.title,
            description: items.buyCoins.description,
        },
        {
            id: 12,
            category: SupportCategory.wallet,
            title: items.monetize.title,
            description: items.monetize.description,
        },
        {
            id: 13,
            category: SupportCategory.wallet,
            title: items.linkRevolut.title,
            description: items.linkRevolut.description,
        },
        {
            id: 14,
            category: SupportCategory.privacyPolicy,
            title: items.privacyPolicyAgreement.title,
            description: items.privacyPolicyAgreement.description,
        },
        {
            id: 15,
            category: SupportCategory.privacy,
            title: items.contentPrivacy.title,
            description: items.contentPrivacy.description,
        },
        {
            id: 16,
            category: SupportCategory.privacy,
            title: items.accountPrivacy.title,
            description: items.accountPrivacy.description,
        },
        {
            id: 17,
            category: SupportCategory.report,
            title: items.reportContent.title,
            description: items.reportContent.description,
        },
        {
            id: 18,
            category: SupportCategory.report,
            title: items.reportAccount.title,
            description: items.reportAccount.description,
        },
        {
            id: 19,
            category: SupportCategory.supportRequest,
            title: items.submitRequest.title,
            description: items.submitRequest.description,
        },
    ], [items]);
};
